import React, { useEffect, useRef, useState } from 'react';
import type { MediaOverlays, StoryItem, User } from '../types';
import MediaEditor from './MediaEditor';
import OverlayLayer from './OverlayLayer';
import { hasOverlayContent } from '../utils/overlays';
import XMarkIcon from './icons/XMarkIcon';
import CameraIcon from './icons/CameraIcon';
import { extractVideoSegment, readVideoMetadata } from '../utils/mediaUtils';

interface UploadNewsPageProps {
  currentUser: User | null;
  onPromptLogin: () => void;
  // Must reject if the upload fails, so this page can show the error and keep the user's selection.
  onAddStory: (item: StoryItem) => void | Promise<void>;
  onClose: () => void;
  initialFile?: File | null;
}

const NEWS_SEGMENT_DURATION = 60;
const IMAGE_DURATION_SECONDS = 5;
const MAX_VIDEO_BYTES = 400 * 1024 * 1024;
const MAX_IMAGE_BYTES = 40 * 1024 * 1024;

const formatBytes = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const formatSeconds = (s: number) => {
  const total = Math.round(s);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

const ImagesIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <circle cx="9" cy="10.5" r="1.6" />
    <path d="m21 16-5-4.5L8 19" />
  </svg>
);

const SplitIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="6" width="7" height="12" rx="2" />
    <rect x="14" y="6" width="7" height="12" rx="2" />
  </svg>
);

const UploadNewsPage: React.FC<UploadNewsPageProps> = ({ currentUser, onPromptLogin, onAddStory, onClose, initialFile }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<{ file: File; url: string } | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [overlays, setOverlays] = useState<MediaOverlays | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [postingStatus, setPostingStatus] = useState('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  // Free the preview's object URL whenever it's replaced and when the page closes.
  useEffect(() => {
    previewUrlRef.current = preview?.url ?? null;
    return undefined;
  }, [preview]);
  useEffect(() => () => { if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current); }, []);

  useEffect(() => { if (initialFile) void acceptFile(initialFile); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVideoFile = preview?.file.type.startsWith('video/') ?? false;
  const segmentCount = videoDuration && videoDuration > NEWS_SEGMENT_DURATION ? Math.ceil(videoDuration / NEWS_SEGMENT_DURATION) : 1;

  const clearPreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
    setVideoDuration(null);
    setOverlays(null);
  };

  const acceptFile = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    if (!isVideo && !isImage) {
      setError('Please choose a photo or a video.');
      return;
    }
    if (file.size > (isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES)) {
      setError(`That file is too large. ${isVideo ? 'Videos' : 'Photos'} can be up to ${formatBytes(isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES)}.`);
      return;
    }
    let duration: number | null = null;
    if (isVideo) {
      try {
        duration = (await readVideoMetadata(file)).duration;
      } catch {
        duration = null;
      }
    }
    if (preview) URL.revokeObjectURL(preview.url);
    setVideoDuration(duration);
    setPreview({ file, url: URL.createObjectURL(file) });
  };

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    void acceptFile(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    void acceptFile(event.dataTransfer.files?.[0]);
  };

  const handleCreateNews = async () => {
    if (!currentUser) { onPromptLogin(); return; }
    if (!preview || isPosting) return;
    setIsPosting(true);
    setError('');
    try {
      const isVideo = preview.file.type.startsWith('video/');
      if (isVideo && videoDuration && videoDuration > NEWS_SEGMENT_DURATION) {
        const total = Math.ceil(videoDuration / NEWS_SEGMENT_DURATION);
        for (let i = 0; i < total; i++) {
          const start = i * NEWS_SEGMENT_DURATION;
          const end = Math.min((i + 1) * NEWS_SEGMENT_DURATION, videoDuration);
          setPostingStatus(`Processing story ${i + 1} of ${total}…`);
          let segFile: File;
          try {
            segFile = await extractVideoSegment(preview.file, start, end);
          } catch {
            // Fallback: upload the original file if splitting fails (e.g. on iOS).
            segFile = preview.file;
          }
          await onAddStory({
            id: `news_${Date.now()}_${i}`,
            type: 'video',
            url: URL.createObjectURL(segFile),
            duration: end - start,
            timestamp: Date.now(),
            file: segFile,
            overlays: hasOverlayContent(overlays) ? overlays : null,
          });
          if (segFile === preview.file) break; // fallback — only upload once
        }
      } else {
        setPostingStatus('Uploading…');
        await onAddStory({
          id: `news_${Date.now()}`,
          type: isVideo ? 'video' : 'image',
          url: preview.url,
          duration: isVideo ? Math.min(videoDuration ?? NEWS_SEGMENT_DURATION, NEWS_SEGMENT_DURATION) : IMAGE_DURATION_SECONDS,
          timestamp: Date.now(),
          file: preview.file,
          overlays: hasOverlayContent(overlays) ? overlays : null,
        });
      }
      onClose();
    } catch (err: any) {
      console.error('Failed to upload news', err);
      setError(err?.message || 'Upload failed. Check your connection and try again.');
      setIsPosting(false);
      setPostingStatus('');
    }
  };

  const ctaLabel = isPosting
    ? (postingStatus || 'Posting…')
    : preview
      ? `Post News${segmentCount > 1 ? ` · ${segmentCount} stories` : ''}`
      : 'Choose a photo or video';

  return (
    <div className="ridaz-fullpage fixed inset-0 z-[6000] flex flex-col bg-app-backdrop text-white">
      <input type="file" ref={fileInputRef} onChange={handleSelect} accept="image/*,video/*" className="hidden" />
      <input type="file" ref={cameraInputRef} onChange={handleSelect} accept="image/*,video/*" capture="environment" className="hidden" />

      {/* Top bar */}
      <header
        className="flex items-center gap-3 border-b border-white/10 bg-black/60 px-4 pb-3 backdrop-blur-md"
        style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}
      >
        <button
          onClick={onClose}
          disabled={isPosting}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition hover:bg-white/15 disabled:opacity-40"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h1 className="text-lg font-black leading-tight">Post News</h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[520px] space-y-5 px-4 py-5">
          {!currentUser && (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/10 px-4 py-3">
              <p className="text-sm font-semibold text-amber-100">Sign in to post to your News.</p>
              <button onClick={onPromptLogin} className="shrink-0 rounded-lg bg-amber-300 px-3 py-1.5 text-xs font-black uppercase text-black hover:bg-amber-200">Sign in</button>
            </div>
          )}

          {preview ? (
            <section aria-label="Preview" className="space-y-3">
              <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black shadow-2xl shadow-black/50">
                {isVideoFile ? (
                  <video src={preview.url} className="max-h-[52vh] w-full object-contain" controls playsInline />
                ) : (
                  <img src={preview.url} alt="News preview" className="max-h-[52vh] w-full object-contain" />
                )}
                <OverlayLayer overlays={overlays} fit="contain" />
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  disabled={isPosting}
                  className={`absolute bottom-3 right-3 z-[3] flex items-center gap-2.5 rounded-2xl bg-lime-400 px-5 py-3 text-base font-black text-black shadow-[0_8px_24px_rgba(0,0,0,0.5)] ring-2 ring-white/40 transition active:scale-95 disabled:opacity-40`}
                  aria-label="Edit with text and drawing"
                >
                  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z" /><path d="M14.5 6.5l3 3" /></svg>
                  {hasOverlayContent(overlays) ? 'Edited ✓' : 'Text & draw'}
                </button>
                <div className="pointer-events-none absolute left-3 top-3 z-[3] flex gap-1.5">
                  <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white backdrop-blur">{isVideoFile ? 'Video' : 'Photo'}</span>
                  <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold text-gray-200 backdrop-blur">{formatBytes(preview.file.size)}</span>
                  {isVideoFile && videoDuration ? <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold text-gray-200 backdrop-blur">{formatSeconds(videoDuration)}</span> : null}
                </div>
                <button
                  onClick={clearPreview}
                  disabled={isPosting}
                  className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur transition hover:bg-black/90 disabled:opacity-40"
                  aria-label="Remove selected media"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isPosting} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 py-3 text-sm font-bold transition hover:bg-white/10 disabled:opacity-40">
                  <ImagesIcon className="h-5 w-5" /> Change
                </button>
                <button type="button" onClick={() => cameraInputRef.current?.click()} disabled={isPosting} className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 py-3 text-sm font-bold transition hover:bg-white/10 disabled:opacity-40">
                  <CameraIcon className="h-5 w-5" /> Retake
                </button>
              </div>

              {segmentCount > 1 && (
                <div className="flex items-start gap-3 rounded-2xl border border-lime-400/30 bg-lime-400/10 px-4 py-3">
                  <SplitIcon className="mt-0.5 h-5 w-5 shrink-0 text-lime-300" />
                  <p className="text-sm text-lime-100">
                    This video is {Math.round(videoDuration || 0)}s long, so it will be posted as <span className="font-black">{segmentCount} stories</span> of up to 60s each.
                  </p>
                </div>
              )}
            </section>
          ) : (
            <section aria-label="Add media" className="space-y-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`flex h-[46vh] min-h-[260px] w-full flex-col items-center justify-center gap-3 rounded-3xl border-2 border-dashed transition active:scale-[0.99] ${isDragging ? 'border-lime-400 bg-lime-400/10' : 'border-white/20 bg-white/[0.03] hover:border-lime-400/60 hover:bg-white/[0.06]'}`}
                aria-label="Upload media"
              >
                <span className="flex h-20 w-20 items-center justify-center rounded-3xl bg-lime-400/15 ring-1 ring-lime-300/30">
                  <ImagesIcon className="h-10 w-10 text-lime-300" />
                </span>
                <span className="text-lg font-black">Upload media</span>
              </button>
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-2.5 rounded-2xl bg-lime-500 py-4 text-base font-black text-black shadow-lg shadow-lime-500/20 transition hover:bg-lime-400 active:scale-[0.99]"
              >
                <CameraIcon className="h-6 w-6" /> Open camera
              </button>
            </section>
          )}

          {error && (
            <p role="alert" className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-200">{error}</p>
          )}

        </div>
      </div>

      {/* Sticky action bar (only once media has been chosen) */}
      {preview && (
      <div
        className="border-t border-white/10 bg-black/70 px-4 pt-3 backdrop-blur-md"
        style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 0.75rem)' }}
      >
        <div className="mx-auto w-full max-w-[520px]">
          <button
            onClick={handleCreateNews}
            disabled={!preview || isPosting}
            className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-lime-500 py-3.5 text-sm font-black uppercase tracking-[0.08em] text-black shadow-lg shadow-lime-500/20 transition hover:bg-lime-400 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-gray-500 disabled:shadow-none"
          >
            {isPosting && <span className="h-4 w-4 animate-spin rounded-full border-2 border-black/30 border-t-black" />}
            {ctaLabel}
          </button>
        </div>
      </div>
      )}
      {isEditing && preview && (
        <MediaEditor
          mediaUrl={preview.url}
          mediaType={isVideoFile ? 'video' : 'image'}
          initial={overlays}
          onCancel={() => setIsEditing(false)}
          onDone={(o) => { setOverlays(o); setIsEditing(false); }}
        />
      )}
    </div>
  );
};

export default UploadNewsPage;
